import { useCallback } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';

import { withTranslation } from '../../../locales/hoc';
import { spacing } from '../../../theme';
import { Modal, ModalProps } from '../Modal';
import { Text } from '../../Text';
import { ModalActions, ActionProps } from '../ModalActions';

type ConfirmModalActionProps = Partial<Omit<ActionProps, 'onPress'>>;
interface ConfirmModalProps extends Omit<ModalProps, 'actions' | 'title' | 'children'> {
	title: string;
	message: string;
	onConfirm: () => void;
	confirmProps?: ConfirmModalActionProps;
	cancelProps?: ConfirmModalActionProps;
}

const ConfirmModalComponent = ({
	onClose,
	onConfirm,
	message,
	confirmProps,
	cancelProps,
	...modalProps
}: ConfirmModalProps) => {
	const { t } = useTranslation();

	const handleOnClose = useCallback(() => {
		onClose && onClose();
	}, [onClose]);

	return (
		<Modal
			actions={
				<ModalActions
					primaryAction={{
						onPress: onConfirm,
						label: t('dialog.yes'),
						variant: Platform.OS === 'web' ? 'filled' : 'outline',
						color: 'danger',
						...confirmProps,
					}}
					secondaryAction={{ onPress: handleOnClose, label: t('dialog.no'), ...cancelProps }}
				/>
			}
			style={styles.root}
			actionsStyle={styles.actions}
			onClose={handleOnClose}
			{...modalProps}
		>
			<Text category="s1">{message}</Text>
		</Modal>
	);
};

const styles = StyleSheet.create({
	root: Platform.select({
		web: {
			maxWidth: 400,
		},
		default: {},
	}),
	actions: Platform.select({
		web: {
			flexDirection: 'row-reverse',
			gap: spacing(2),
		},
		default: {},
	}),
});

const ConfirmModal = withTranslation(ConfirmModalComponent);

export { ConfirmModal, ConfirmModalProps, ConfirmModalActionProps };
